import React from "react";
import { FiRefreshCcw, FiLoader } from "react-icons/fi";

const formatMondayDate = (dateString) => {
  if (!dateString || dateString === "All Monday") return "All Mondays (side by side)";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("en-GB", { day: 'numeric', month: 'short', year: 'numeric' });
};

export const EDAFilters = ({ filters, lists, handleReset, loading }) => {
  const {
    selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth,
    selectedDate, setSelectedDate,
    selectedComponent, setSelectedComponent,
    selectedPlant, setSelectedPlant,
    selectedGrouping, setSelectedGrouping,
    searchQuery, setSearchQuery
  } = filters;

  const {
    yearsList, monthsList, mondaysList,
    componentsList, plantsList, groupingsList
  } = lists;

  const activeFilters = [];
  if (selectedComponent !== "all") activeFilters.push(selectedComponent);
  if (selectedPlant !== "all") activeFilters.push(`Plant ${selectedPlant}`);
  if (selectedGrouping !== "all") activeFilters.push(selectedGrouping);
  if (searchQuery.trim() !== "") activeFilters.push(`"${searchQuery}"`);
  const scopeLabel = activeFilters.length > 0 ? activeFilters.join(" · ") : "All items";

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 items-end">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-bold uppercase tracking-wide opacity-80 text-slate-400">Year</label>
          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
            {yearsList.map((y, i) => <option key={i} value={y.Year}>{y.Year}</option>)}
          </select>
        </div>
        
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-bold uppercase tracking-wide opacity-80 text-slate-400">Month</label>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
            {monthsList.map((m, i) => <option key={i} value={m.Month_No}>{m.Month_Name}</option>)}
          </select>
        </div>
        
        <div className="flex flex-col gap-1.5 xl:col-span-1">
          <label className="text-sm font-bold uppercase tracking-wide opacity-80 text-slate-400 truncate">Date (Monday)</label>
          <select value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
            {mondaysList.map((m, i) => {
              const val = m.Monday_Date === "All Monday" ? "all" : m.Monday_Date;
              const label = formatMondayDate(m.Monday_Date);
              return <option key={i} value={val}>{label}</option>;
            })}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-bold uppercase tracking-wide opacity-80 text-slate-400">Component</label>
          <select value={selectedComponent} onChange={(e) => setSelectedComponent(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
            <option value="all">All components</option>
            {componentsList.map((c, i) => <option key={i} value={c.Component}>{c.Component}</option>)}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-bold uppercase tracking-wide opacity-80 text-slate-400">Plant</label>
          <select value={selectedPlant} onChange={(e) => setSelectedPlant(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
            <option value="all">All plants</option>
            {plantsList.map((p, i) => <option key={i} value={p.Plnt}>{p.Plnt}</option>)}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-bold uppercase tracking-wide opacity-80 text-slate-400">Grouping</label>
          <select value={selectedGrouping} onChange={(e) => setSelectedGrouping(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
            <option value="all">All groupings</option>
            {groupingsList.map((g, i) => <option key={i} value={g.Grouping}>{g.Grouping}</option>)}
          </select>
        </div>

        <div className="flex flex-col gap-1.5 xl:col-span-1">
          <label className="text-sm font-bold uppercase tracking-wide opacity-80 text-slate-400 truncate">Search material / desc.</label>
          <input type="text" placeholder="e.g. bolt, 155431..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-slate-400" />
        </div>
      </div>

      <div className="flex items-center gap-3 mt-1">
        <button onClick={handleReset} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
          <FiRefreshCcw className="h-3 w-3" />
          Reset filters
        </button>
        <span className="text-xs text-slate-500">Scope: <span className="text-slate-700 font-medium">{scopeLabel}</span></span>
        {loading && (
          <div className="ml-auto inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
            <FiLoader className="h-3.5 w-3.5 animate-spin" />
            Updating...
          </div>
        )}
      </div>
    </div>
  );
};
